<template>
  <div class="modern-header">
    <div class="header-container">
      <div class="breadcrumb-section">
        <nav class="breadcrumb">
          <button type="button" class="breadcrumb-item" @click="$router.push('/dashboard')">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
            </svg>
            {{ t('common.breadcrumb.dashboard') }}
          </button>
          <span class="breadcrumb-separator">/</span>
          <button
            v-if="section"
            type="button"
            class="breadcrumb-item"
            @click="$router.push('/estruturacao')"
          >
            {{ t('estruturacao.breadcrumb') }}
          </button>
          <template v-if="section">
            <span class="breadcrumb-separator">/</span>
          </template>
          <span class="breadcrumb-current">{{ titleText }}</span>
        </nav>
      </div>

      <div class="title-section">
        <div class="title-content">
          <div
            class="title-icon"
            :style="iconStyle"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="iconPath" />
            </svg>
          </div>
          <div class="title-text">
            <h1 class="main-title">{{ titleText }}</h1>
            <p class="subtitle">{{ subtitleText }}</p>
          </div>
        </div>

        <div class="workspace-badge" :style="badgeStyle">
          <div class="badge-icon" :style="badgeIconStyle">
            <span class="badge-number">{{ badgeLabel }}</span>
          </div>
          <span class="badge-text">{{ badgeText }}</span>
        </div>
      </div>

      <div class="actions-section">
        <button
          v-if="section"
          type="button"
          class="action-button secondary"
          @click="$router.push('/estruturacao')"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
          {{ t('estruturacao.backToHub') }}
        </button>
        <template v-else>
          <div class="action-stat">
            <span class="action-stat__value">{{ unlockedCount }}/{{ totalSections }}</span>
            <span class="action-stat__label">{{ t('estruturacao.areasUnlocked') }}</span>
          </div>
        </template>
        <button
          type="button"
          class="action-button primary"
          :style="primaryButtonStyle"
          @click="handlePrimaryAction"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7"/>
          </svg>
          {{ primaryActionLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { ESTRUTURACAO_SECTIONS } from '../../constants/estruturacaoSections'

const props = defineProps({
  section: {
    type: Object,
    default: null
  },
  unlockedCount: {
    type: Number,
    default: 0
  }
})

const { t } = useI18n()

const totalSections = ESTRUTURACAO_SECTIONS.length

const hubAccent = {
  accentFrom: '#6366f1',
  accentTo: '#4f46e5'
}

const accent = computed(() => props.section || hubAccent)

const titleText = computed(() =>
  props.section ? t(props.section.labelKey) : t('estruturacao.title')
)

const subtitleText = computed(() =>
  props.section ? t(props.section.subtitleKey) : t('estruturacao.subtitle')
)

const iconPath = computed(() =>
  props.section?.iconPath
    || 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z'
)

const badgeLabel = computed(() =>
  props.section?.badge || String(totalSections).padStart(2, '0')
)

const badgeText = computed(() =>
  props.section ? t('estruturacao.sectionBadge') : t('estruturacao.badgeModules')
)

const primaryActionLabel = computed(() =>
  props.section ? t('estruturacao.exploreModules') : t('estruturacao.viewAreas')
)

const iconStyle = computed(() => ({
  background: `linear-gradient(135deg, ${accent.value.accentFrom} 0%, ${accent.value.accentTo} 100%)`,
  boxShadow: `0 4px 16px color-mix(in srgb, ${accent.value.accentFrom} 45%, transparent)`
}))

const badgeStyle = computed(() => ({
  background: `color-mix(in srgb, ${accent.value.accentFrom} 12%, transparent)`,
  borderColor: `color-mix(in srgb, ${accent.value.accentFrom} 35%, transparent)`,
  color: accent.value.accentFrom
}))

const badgeIconStyle = computed(() => ({
  background: `linear-gradient(135deg, ${accent.value.accentFrom} 0%, ${accent.value.accentTo} 100%)`
}))

const primaryButtonStyle = computed(() => ({
  background: `linear-gradient(135deg, ${accent.value.accentFrom} 0%, ${accent.value.accentTo} 100%)`,
  boxShadow: `0 4px 14px color-mix(in srgb, ${accent.value.accentFrom} 40%, transparent)`
}))

const handlePrimaryAction = () => {
  if (props.section) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return
  }
  const content = document.querySelector('.estruturacao-content')
  content?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>

<style scoped>
.modern-header {
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-primary);
  padding: 24px 0;
}

.header-container {
  width: 100%;
  padding: 0 24px;
}

.breadcrumb-section {
  margin-bottom: 16px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  flex-wrap: wrap;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
}

.breadcrumb-item:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.breadcrumb-separator {
  color: var(--text-tertiary);
  font-weight: 500;
}

.breadcrumb-current {
  color: var(--text-primary);
  font-weight: 600;
  padding: 6px 12px;
  background: var(--bg-secondary);
  border-radius: 4px;
}

.title-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.title-content {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
}

.title-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 4px;
  color: white;
  flex-shrink: 0;
}

.main-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.2;
}

.subtitle {
  font-size: 1rem;
  color: var(--text-secondary);
  margin: 4px 0 0;
}

.workspace-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  border: 1px solid transparent;
}

.badge-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  color: #fff;
  flex-shrink: 0;
}

.badge-number {
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
}

.actions-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.action-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 14px;
  border-radius: 4px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
}

.action-stat__value {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}

.action-stat__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-secondary);
}

.action-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  text-decoration: none;
  color: #fff;
}

.action-button.secondary {
  background: var(--bg-secondary);
  border-color: var(--border-primary);
  color: var(--text-primary);
}

.action-button.secondary:hover {
  border-color: var(--accent-primary);
  color: var(--accent-primary);
}

.action-button.primary:hover {
  transform: translateY(-1px);
  filter: brightness(1.05);
}

@media (max-width: 768px) {
  .main-title {
    font-size: 1.5rem;
  }

  .title-section {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
